import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { getTransactionsPendingApprovalAsync } from '../transactionMandateV2/getPendingTransactionsSlice';

interface IApproveTransactionRequest {
  transactionId: string;
  pin: string;
}

interface IApproveTransactionResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IApproveTransactionResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const approveTransactionAsync = createAsyncThunk(
  'transaction/approve',
  async (
    values: IApproveTransactionRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IApproveTransactionResponse>(
        `transaction/outlet-mandates/${values.transactionId}/approve`,
        { pin: values.pin },
        config,
      );

      dispatch(getTransactionsPendingApprovalAsync({}));

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Transaction successfully approved',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApproveTransactionResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const transactionMandateCreationSlice = createSlice({
  name: 'transaction/approve',
  initialState,
  reducers: {
    resetTransactionApprove(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(approveTransactionAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(approveTransactionAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IApproveTransactionResponse;
      })
      .addCase(approveTransactionAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IApproveTransactionResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetTransactionApprove } =
  transactionMandateCreationSlice.actions;
export default transactionMandateCreationSlice.reducer;
